import { useSelector } from 'react-redux';
import {
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../../../reducers/documentation/documentationReducer';
import { useMemo } from 'react';
import { isItemActive } from '../../helpers';
import { automationTypes } from '../../../parser/parserUtils';
import { DocumentationTabTypes } from '../../../../types/enums/DocumentationTabTypes';
import { selectDocumentationConfigurationItems } from '../../../../reducers/global/parserReducer';
import { ActiveState } from '../../activity-state/helpers';
import { DocumentationFiltersTypesMap } from './filterTypes';
import { useDocumentationFiltersContext } from './DocumentationFiltersContext';
import { ConfigurationType } from '@server/parser';

export const useGetFilteredConfigurationItems = (crmOrgId: string) => {
  const { filterValues } = useDocumentationFiltersContext();
  const activeOnly =
    filterValues?.[DocumentationFiltersTypesMap.activeState]?.selectedValues[0] !==
    ActiveState.activeAndInactive;

  const configurationItems = useSelector(selectDocumentationConfigurationItems(crmOrgId));
  const activeDocumentationTab = useSelector(selectSingleObjectActiveTab);
  const objectName = useSelector(selectSingleObject);

  const filteredConfigurationItems = useMemo(() => {
    const isActiveAndInactive = !activeOnly;

    if (
      !isActiveAndInactive ||
      !activeDocumentationTab ||
      !ACTIVE_INACTIVE_RELEVANT_DOCUMENTATION_TAB_TYPES.includes(activeDocumentationTab) ||
      !objectName
    ) {
      return configurationItems?.filter(isItemActive) ?? [];
    }

    /**
     * we want to get inactive ONLY for current objectName and ruleTypes
     */
    return (
      configurationItems?.filter((item) => {
        if (
          item.objectApiNames.includes(objectName.objectType) &&
          DocumentationTabTypeToRuleType[activeDocumentationTab]?.includes(
            item.parentType as ConfigurationType,
          )
        ) {
          return item;
        }
        return isItemActive(item);
      }) ?? []
    );
  }, [activeDocumentationTab, objectName, activeOnly, configurationItems]);

  return filteredConfigurationItems;
};

export const ACTIVE_INACTIVE_RELEVANT_DOCUMENTATION_TAB_TYPES = [
  DocumentationTabTypes.APEX,
  DocumentationTabTypes.VALIDATION_RULES,
  DocumentationTabTypes.SF_AUTOMATIONS,
];

const DocumentationTabTypeToRuleType: { [key in any]: readonly ConfigurationType[] } = {
  [DocumentationTabTypes.APEX]: [ConfigurationType.apexTriggers, ConfigurationType.apexClasses],
  [DocumentationTabTypes.VALIDATION_RULES]: [ConfigurationType.validationRules],
  [DocumentationTabTypes.SF_AUTOMATIONS]: automationTypes,
};
